"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { Search, ChevronRight, Calendar, CircleAlert } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { FormProvider } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Pagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
function NewsBoardContent() {
  const {
    posts,
    loading,
    error,
    setQuery,
    currentPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage,
    prevPage
  } = usePosts();
  const [searchTerm, setSearchTerm] = useState("");
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setQuery({
      search: searchTerm,
      page: 1
    });
  };
  return <div className="flex flex-col gap-8">
      {}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-white/10 pb-8">
        <div className="max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-foreground mb-4">
            Aggiornamenti e Notizie
          </h2>
          <p className="text-primary-foreground/70 text-lg">
            Rimani informato con le ultime novità legali, approfondimenti e aggiornamenti dal nostro studio.
          </p>
        </div>

        <FormProvider>
          <form onSubmit={handleSearch} className="relative w-full md:w-80 shrink-0">
            <Input type="search" placeholder="Cerca articoli..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full bg-white/5 border-white/20 text-primary-foreground placeholder:text-primary-foreground/50 pr-12 focus-visible:ring-ring h-12 rounded-xl" />
            <Button type="submit" size="icon" variant="ghost" className="absolute right-1 top-1 h-10 w-10 text-ring hover:bg-white/10 hover:text-ring">
              <Search className="size-5" />
              <span className="sr-only">Cerca</span>
            </Button>
          </form>
        </FormProvider>
      </div>

      {}
      <div className="min-h-[400px]">
        {loading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4">
                <Skeleton className="h-48 w-full rounded-xl bg-white/10" />
                <Skeleton className="h-6 w-3/4 bg-white/10" />
                <Skeleton className="h-4 w-1/2 bg-white/10" />
                <Skeleton className="h-20 w-full bg-white/10 mt-2" />
              </div>)}
          </div> : error ? <div className="flex flex-col items-center justify-center py-20 text-center bg-white/5 rounded-2xl border border-white/10">
            <CircleAlert className="size-12 text-destructive mb-4 opacity-80" />
            <p className="text-primary-foreground/80 text-lg">
              Si è verificato un errore durante il caricamento degli articoli.
            </p>
          </div> : posts.length === 0 ? <div className="flex flex-col items-center justify-center py-20 text-center bg-white/5 rounded-2xl border border-white/10">
            <Search className="size-12 text-primary-foreground/30 mb-4" />
            <h3 className="text-xl font-medium text-primary-foreground mb-2">Nessun articolo trovato</h3>
            <p className="text-primary-foreground/60">
              Prova a modificare i termini di ricerca per trovare quello che cerchi.
            </p>
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post, i) => <Card key={post.id} data-index={i} className="bg-white/5 backdrop-blur-md border-white/10 text-primary-foreground overflow-hidden flex flex-col h-full hover:border-ring/50 transition-all duration-300 group">
                {post.featuredImage && <div className="w-full h-48 overflow-hidden relative">
                    <div className="absolute inset-0 bg-primary/20 group-hover:bg-transparent transition-colors z-10" />
                    <img src={post.featuredImage} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>}
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2 text-ring text-xs font-medium mb-3 uppercase tracking-wider">
                    <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                    <time>
                      {post.date ? new Date(post.date).toLocaleDateString("it-IT", {
                  day: "numeric",
                  month: "long",
                  year: "numeric"
                }) : ""}
                    </time>
                  </div>
                  <CardTitle className="text-xl font-serif leading-tight line-clamp-2 group-hover:text-ring transition-colors">
                    {post.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow pb-4">
                  <div className="text-primary-foreground/70 text-sm line-clamp-3 leading-relaxed" dangerouslySetInnerHTML={{
              __html: post.excerpt || ""
            }} />
                </CardContent>
                <CardFooter className="pt-0">
                  <Link to={post.link || "#"} className="inline-flex items-center gap-1.5 text-ring hover:text-ring/80 text-sm font-semibold transition-colors mt-auto">
                    Leggi l'articolo <ChevronRight className="size-4" />
                  </Link>
                </CardFooter>
              </Card>)}
          </div>}
      </div>

      {}
      {!loading && totalPages > 1 && <div className="mt-8 pt-8 border-t border-white/10 flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious href="#" onClick={e => {
              e.preventDefault();
              if (hasPrev) prevPage();
            }} className={`border-white/20 text-primary-foreground hover:bg-white/10 hover:text-primary-foreground ${!hasPrev ? "opacity-50 pointer-events-none" : ""}`} />
              </PaginationItem>
              <PaginationItem>
                <span className="text-sm font-medium text-primary-foreground/80 px-4 flex items-center h-10">
                  Pagina {currentPage} di {totalPages}
                </span>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext href="#" onClick={e => {
              e.preventDefault();
              if (hasNext) nextPage();
            }} className={`border-white/20 text-primary-foreground hover:bg-white/10 hover:text-primary-foreground ${!hasNext ? "opacity-50 pointer-events-none" : ""}`} />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>}
    </div>;
}
export default function NewsBoard() {
  const wp_query_params = {
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="newsboard" className="bg-background w-full py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        
      </div>
    </section>;
}